import { type RefObject, useLayoutEffect } from 'react';

const DEFAULT_MAX_PX = 52;
const DEFAULT_TRAVEL = 96;

/** Match add/removeEventListener (capture flag must be identical). */
const SCROLL_LISTENER_OPTS: AddEventListenerOptions = { passive: true, capture: true };
const PASSIVE_OPTS: AddEventListenerOptions = { passive: true };

export type ScrollParallaxOptions = {
  disabled?: boolean;
  /** When false, listeners are removed (e.g. conditional section unmounted). Default true. */
  enabled?: boolean;
  /** Clamp for translateY in px (absolute value). Default 52. */
  maxPx?: number;
  /** How much scroll position moves the element; paired with `strength`. Default 96. */
  travelScale?: number;
  /** Reverse direction (e.g. alternate foreground / background). */
  invert?: boolean;
};

/**
 * TranslateY tied to scroll while the element is near the viewport.
 * Sets `--ml-py` on **ref.current** (use a non-transformed `.ml-parallaxScope` wrapper;
 * put `.ml-parallaxLift` on a child so getBoundingClientRect is not feedback-distorted).
 */
export function useScrollParallax<T extends HTMLElement>(
  ref: RefObject<T | null>,
  strength = 0.12,
  {
    disabled = false,
    enabled = true,
    maxPx = DEFAULT_MAX_PX,
    travelScale = DEFAULT_TRAVEL,
    invert = false,
  }: ScrollParallaxOptions = {}
) {
  useLayoutEffect(() => {
    if (disabled || !enabled || typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const scope = ref.current;
    if (!scope) return;

    let rafId: number | null = null;

    const tick = () => {
      const node = ref.current;
      if (!node) return;
      const r = node.getBoundingClientRect();
      const vh = window.innerHeight;
      const boxH = r.height > 0 ? r.height : node.offsetHeight;
      if (boxH <= 0 || vh <= 0) {
        node.style.setProperty('--ml-py', '0px');
        return;
      }
      const mid = r.top + boxH / 2;
      const t = (mid - vh * 0.5) / (vh * 0.78);
      let px = -t * strength * travelScale;
      if (invert) px = -px;
      px = Math.max(-maxPx, Math.min(maxPx, px));
      node.style.setProperty('--ml-py', `${px.toFixed(2)}px`);
    };

    const schedule = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        tick();
      });
    };

    tick();

    window.addEventListener('scroll', schedule, SCROLL_LISTENER_OPTS);
    window.addEventListener('resize', schedule, PASSIVE_OPTS);
    window.addEventListener('pageshow', schedule, PASSIVE_OPTS);

    const scrollingEl = document.scrollingElement;
    if (scrollingEl) {
      scrollingEl.addEventListener('scroll', schedule, PASSIVE_OPTS);
    }

    const visualViewport = window.visualViewport;
    if (visualViewport) {
      visualViewport.addEventListener('scroll', schedule, PASSIVE_OPTS);
      visualViewport.addEventListener('resize', schedule, PASSIVE_OPTS);
    }

    if ('onscrollend' in window) {
      window.addEventListener('scrollend', schedule, PASSIVE_OPTS);
    }

    const ro = new ResizeObserver(() => schedule());
    ro.observe(scope);

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', schedule, SCROLL_LISTENER_OPTS);
      window.removeEventListener('resize', schedule, PASSIVE_OPTS);
      window.removeEventListener('pageshow', schedule, PASSIVE_OPTS);
      if (scrollingEl) {
        scrollingEl.removeEventListener('scroll', schedule, PASSIVE_OPTS);
      }
      if (visualViewport) {
        visualViewport.removeEventListener('scroll', schedule, PASSIVE_OPTS);
        visualViewport.removeEventListener('resize', schedule, PASSIVE_OPTS);
      }
      if ('onscrollend' in window) {
        window.removeEventListener('scrollend', schedule, PASSIVE_OPTS);
      }
      ro.disconnect();
      scope.style.removeProperty('--ml-py');
    };
  }, [ref, strength, disabled, enabled, maxPx, travelScale, invert]);
}
